import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

import toast from "components/partials/toast/toast";

import { Unpersisted } from "types/utility";

import { getTouchpointBatch } from "generated/api/touchpoint-batch";
import {
  AddToBatchRequest,
  GetAll2Params,
  PagedModelTouchpointBatchDTO,
  PagedModelTouchpointVersionBatchDTO,
  TouchpointBatchDTO,
  TouchpointBatchRequest,
  TouchpointVersionBatchDTO,
} from "generated/schemas";

import { batchKeys, touchpointKeys, touchpointVersionKeys } from "hooks/api/query-keys";

import { mapDtoStatusToTouchpointStatus, mapDtoTypeToTouchpointType } from "utilities/api-mappers";
import { mapPage } from "utilities/pagination";

import {
  BatchItem,
  BatchStatus,
  TouchpointItem,
} from "containers/admin/clients/client/approval-queue/approval-queue.types";
import { PaginatedRequestOptions, PaginatedResponse } from "types/pagination";

const batchApi = getTouchpointBatch();

type ApiErrorData = { title?: string; detail?: string; message?: string };

const getResponseData = (error: unknown): ApiErrorData | undefined =>
  (error as AxiosError<ApiErrorData>)?.response?.data;

const mapBatch = (dto: TouchpointBatchDTO): BatchItem => ({
  id: dto.id,
  name: dto.name,
  description: dto.description,
  totalCount: dto.totalCount,
  approvedCount: dto.approvedCount,
  dueDate: dto.dueDate,
  status: dto.status,
  lastModifiedDate: dto.lastModifiedDate,
  mondayBoardLink: dto.mondayBoardLink ?? "",
});

const mapBatchPage = (page: PagedModelTouchpointBatchDTO): PaginatedResponse<BatchItem> =>
  mapPage(page, mapBatch);

const mapBatchVersion = (dto: TouchpointVersionBatchDTO): TouchpointItem => ({
  id: dto.id,
  type: mapDtoTypeToTouchpointType(dto.type),
  status: mapDtoStatusToTouchpointStatus(dto.status),
  touchpointName: dto.touchpointName ?? "",
  campaignName: dto.campaignName ?? "",
  addedToBatchDate: dto.addedToBatchDate,
  version: dto.version ?? 0,
  openCommentCount: dto.openCommentCount,
  batchId: dto.touchpointBatchId,
  campaignId: dto.campaignId ?? "",
  touchpointId: dto.touchpointId,
  applyClientTheme: dto.applyClientTheme,
  description: dto.description ?? "",
});

const mapBatchVersionPage = (
  page: PagedModelTouchpointVersionBatchDTO,
): PaginatedResponse<TouchpointItem> => mapPage(page, mapBatchVersion);

const toListParams = (options: PaginatedRequestOptions): GetAll2Params => {
  const sort = options.sort ? [options.sort] : undefined;

  if (options.unpaged) {
    return { unpaged: true, sort, q: options.search };
  }

  return {
    page: options.page,
    size: options.size,
    sort,
    q: options.search,
  };
};

type UseBatchesParams = PaginatedRequestOptions & {
  clientId?: string;
  /** Only return batches with this status (sent as status.in query param). */
  statusEquals?: BatchStatus;
};

type BatchMutationContext = {
  clientId?: string;
};

type BatchIdVariables = {
  batchId: string;
};

type EditBatchVariables = {
  batchId: string;
  attributes: Partial<BatchItem>;
};

type AddTouchpointToBatchVariables = {
  batchId: string;
  attributes: AddToBatchRequest;
};

type RemoveTouchpointFromBatchVariables = {
  batchId: string;
  versionId: string;
};

/**
 * Imperative fetch for a single batch. Mirrors the legacy model behaviour: it
 * surfaces an error toast and rejects so call-sites can fall back to an empty
 * state.
 */
export const fetchBatchDetails = async ({
  clientId,
  batchId,
}: {
  clientId: string;
  batchId: string;
}): Promise<BatchItem> => {
  try {
    const dto = await batchApi.getOne({ clientId, batchId });

    return mapBatch(dto);
  } catch (error) {
    toast.error({
      data: {
        title: "Failed to get batch details",
        message: getResponseData(error)?.title,
      },
    });

    throw error;
  }
};

export const fetchBatchTouchpointVersions = async ({
  clientId,
  batchId,
  options,
}: {
  clientId: string;
  batchId: string;
  options: PaginatedRequestOptions;
}): Promise<PaginatedResponse<TouchpointItem>> => {
  try {
    const page = await batchApi.getAllBatchVersions({ clientId, batchId }, toListParams(options));

    return mapBatchVersionPage(page);
  } catch (error) {
    toast.error({
      data: {
        title: "Failed to get batch creative versions",
        message: getResponseData(error)?.title,
      },
    });

    throw error;
  }
};

export const useBatches = <TData = PaginatedResponse<BatchItem>>(
  params: UseBatchesParams,
  options?: Omit<
    UseQueryOptions<PaginatedResponse<BatchItem>, Error, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const { clientId, statusEquals, ...listParams } = params;

  return useQuery({
    queryKey: batchKeys.list(clientId ?? "", {
      ...listParams,
      ...(statusEquals && { statusEquals }),
    }),
    queryFn: async () => {
      if (!clientId) {
        throw new Error("clientId is required for useBatches queryFn");
      }

      const page = await batchApi.getAll2(
        { clientId },
        {
          ...toListParams(listParams),
          ...(statusEquals && { "status.in": [statusEquals] }),
        },
      );

      return mapBatchPage(page);
    },
    ...options,
    enabled: Boolean(clientId) && (options?.enabled ?? true),
  });
};

type UseBatchParams = {
  clientId?: string;
  batchId?: string;
};

export const useBatch = <TData = BatchItem>(
  params: UseBatchParams,
  options?: Omit<UseQueryOptions<BatchItem, Error, TData>, "queryKey" | "queryFn">,
) => {
  const { clientId, batchId } = params;

  return useQuery({
    queryKey: batchKeys.detail(clientId ?? "", batchId ?? ""),
    queryFn: async () => {
      if (!clientId || !batchId) {
        throw new Error("clientId and batchId are required for useBatch queryFn");
      }

      const dto = await batchApi.getOne({ clientId, batchId });

      return mapBatch(dto);
    },
    ...options,
    enabled: Boolean(clientId) && Boolean(batchId) && (options?.enabled ?? true),
  });
};

export const useCreateBatch = ({ clientId }: BatchMutationContext) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (attributes: Unpersisted<BatchItem>) => {
      if (!clientId) {
        throw new Error("clientId is required for useCreateBatch mutation");
      }

      const dto = await batchApi.create3(
        { clientId },
        attributes as unknown as TouchpointBatchRequest,
      );

      return mapBatch(dto);
    },
    onSuccess: () => {
      toast.success({ data: { title: "Created batch" } });
      queryClient.invalidateQueries({ queryKey: batchKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to create batch" } });
    },
  });
};

export const useEditBatch = ({ clientId }: BatchMutationContext) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ batchId, attributes }: EditBatchVariables) => {
      if (!clientId) {
        throw new Error("clientId is required for useEditBatch mutation");
      }

      const dto = await batchApi.update1(
        { clientId, batchId },
        attributes as unknown as TouchpointBatchRequest,
      );

      return mapBatch(dto);
    },
    onSuccess: () => {
      toast.success({ data: { title: "Edited batch" } });
      queryClient.invalidateQueries({ queryKey: batchKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to edit batch" } });
    },
  });
};

export const useSendBatchForReview = ({ clientId }: BatchMutationContext) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ batchId }: BatchIdVariables) => {
      if (!clientId) {
        throw new Error("clientId is required for useSendBatchForReview mutation");
      }

      return batchApi.changeStatus({ clientId, batchId }, { status: "IN_REVIEW" });
    },
    onSuccess: () => {
      toast.success({ data: { title: "Sent batch for review" } });
      queryClient.invalidateQueries({ queryKey: batchKeys.lists() });
    },
    onError: () => {
      toast.error({
        data: {
          title: "Failed to send batch for review",
          message: "Verify that there are no published or archived versions in the batch",
        },
      });
    },
  });
};

export const useApproveBatch = ({ clientId }: BatchMutationContext) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ batchId }: BatchIdVariables) => {
      if (!clientId) {
        throw new Error("clientId is required for useApproveBatch mutation");
      }

      const dto = await batchApi.changeStatus({ clientId, batchId }, { status: "APPROVED" });

      return mapBatch(dto);
    },
    onSuccess: () => {
      toast.success({ data: { title: "Approved batch" } });
      queryClient.invalidateQueries({ queryKey: batchKeys.lists() });
    },
    onError: (error) => {
      const data = getResponseData(error);
      toast.error({
        data: {
          title: "Failed to approve batch",
          message: data?.detail || data?.title,
        },
      });
    },
  });
};

export const useDeleteBatch = ({ clientId }: BatchMutationContext) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ batchId }: BatchIdVariables) => {
      if (!clientId) {
        throw new Error("clientId is required for useDeleteBatch mutation");
      }

      return batchApi.delete1({ clientId, batchId });
    },
    onSuccess: () => {
      toast.success({ data: { title: "Deleted batch" } });
      queryClient.invalidateQueries({ queryKey: batchKeys.lists() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to delete batch" } });
    },
  });
};

export const useAddTouchpointToBatch = ({ clientId }: BatchMutationContext) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ batchId, attributes }: AddTouchpointToBatchVariables) => {
      if (!clientId) {
        throw new Error("clientId is required for useAddTouchpointToBatch mutation");
      }

      return batchApi.addTouchpointVersionToBatch({ clientId, batchId }, attributes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: batchKeys.lists() });
      queryClient.invalidateQueries({ queryKey: touchpointVersionKeys.all });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.latestBatches() });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.details() });
    },
    onError: (error) => {
      toast.error({
        data: {
          title: "Failed to add creative to batch",
          message: getResponseData(error)?.message,
        },
      });
    },
  });
};

export const useRemoveTouchpointFromBatch = ({ clientId }: BatchMutationContext) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ batchId, versionId }: RemoveTouchpointFromBatchVariables) => {
      if (!clientId) {
        throw new Error("clientId is required for useRemoveTouchpointFromBatch mutation");
      }

      return batchApi.removeTouchpointVersionFromBatch({ clientId, batchId, versionId });
    },
    onSuccess: () => {
      toast.success({ data: { title: "Removed creative from batch" } });
      queryClient.invalidateQueries({ queryKey: batchKeys.lists() });
      queryClient.invalidateQueries({ queryKey: touchpointVersionKeys.all });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.details() });
    },
    onError: () => {
      toast.error({ data: { title: "Failed to remove creative from batch" } });
    },
  });
};
